import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Loader,
  Terminal,
  X,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { USER_COMPANIES_QUERY } from '@/components/root/gql'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Muted } from '@/components/sv/typographies'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { GET_API_KEY_ROLES_AND_PERMISSIONS } from '@/routes/api-keys-v2/create-api-key-sheet'
import {
  PermissionSelector,
  type CompanyResources,
} from '@/routes/api-keys-v2/create-api-key-sheet/permission-selector'
import {
  authorizeSchema,
  type AuthorizeFormValues,
} from '@/routes/authorize/schema'
import type { UserCompaniesQuery } from '@/components/root/gql.types.gen'
import type {
  GetApiKeyRolesAndPermissionsQuery,
  GetApiKeyRolesAndPermissionsQueryVariables,
} from '@/routes/api-keys-v2/create-api-key-sheet/index.types.gen'
import type {
  ApproveDeviceCodeMutation,
  ApproveDeviceCodeMutationVariables,
  DenyDeviceCodeMutation,
  DenyDeviceCodeMutationVariables,
  DeviceCodeQuery,
  DeviceCodeQueryVariables,
} from '@/routes/authorize/authorize-form.types.gen'

const DEVICE_CODE_QUERY = gql`
  query DeviceCode($code: String!) {
    deviceCode(code: $code) {
      id
      code
      status
      expiresAt
      createdAt
    }
  }
`

const APPROVE_DEVICE_CODE = gql`
  mutation ApproveDeviceCode(
    $code: String!
    $idCompany: String!
    $name: String!
    $idRoles: [String!]
    $capabilities: [ApiKeyCapabilityInput!]
  ) {
    approveDeviceCode(
      code: $code
      idCompany: $idCompany
      name: $name
      idRoles: $idRoles
      capabilities: $capabilities
    ) {
      success
      error
    }
  }
`

const DENY_DEVICE_CODE = gql`
  mutation DenyDeviceCode($code: String!) {
    denyDeviceCode(code: $code) {
      success
      error
    }
  }
`

type Step = 'company' | 'permissions' | 'success' | 'denied'

const ALLOWED_CALLBACK_ORIGINS = ['https://mcp.sevalla.com']

const isAllowedCallback = (url: string): boolean => {
  try {
    const parsed = new URL(url)
    return ALLOWED_CALLBACK_ORIGINS.includes(parsed.origin)
  } catch {
    return false
  }
}

type Props = {
  code: string
  appName: string
  callback: string
}

const ANIMATION_PROPS = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.2 },
}

const SuccessState = ({ appName }: { appName: string }) => (
  <motion.div
    key="success"
    className="flex flex-col items-center gap-4 py-8"
    {...ANIMATION_PROPS}
  >
    <motion.div
      className="bg-emerald-100 dark:bg-emerald-950 flex size-16 items-center justify-center rounded-full"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <Check className="size-8 text-emerald-600" />
    </motion.div>
    <div className="space-y-1 text-center">
      <p className="text-lg font-medium">Authorization successful</p>
      <Muted>You can return to {appName || 'the application'}.</Muted>
    </div>
  </motion.div>
)

const DeniedState = ({ appName }: { appName: string }) => (
  <motion.div
    key="denied"
    className="flex flex-col items-center gap-4 py-8"
    {...ANIMATION_PROPS}
  >
    <motion.div
      className="bg-destructive/10 flex size-16 items-center justify-center rounded-full"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <X className="text-destructive size-8" />
    </motion.div>
    <div className="space-y-1 text-center">
      <p className="text-lg font-medium">Authorization denied</p>
      <Muted>
        {appName || 'The application'} was not granted access to your account.
      </Muted>
    </div>
  </motion.div>
)

const ExpiredState = ({ appName }: { appName: string }) => (
  <div className="flex flex-col items-center gap-4 py-8 text-center">
    <div className="bg-destructive/10 flex size-12 items-center justify-center rounded-full">
      <AlertTriangle className="text-destructive size-6" />
    </div>
    <div className="space-y-1">
      <p className="text-sm font-medium">This device code has expired.</p>
      <Muted>Please try again from {appName || 'the application'}.</Muted>
    </div>
    <a href="/" className="text-primary text-sm underline underline-offset-4">
      Return to dashboard
    </a>
  </div>
)

export const AuthorizeForm = ({ code, appName, callback }: Props) => {
  const [isPending, startTransition] = useTransition()

  const { data: deviceCodeData } = useSuspenseQuery<
    DeviceCodeQuery,
    DeviceCodeQueryVariables
  >(DEVICE_CODE_QUERY, { variables: { code } })

  const deviceCode = deviceCodeData?.deviceCode
  const isExpired = !deviceCode || deviceCode.status !== 'PENDING'

  const { data: userData } =
    useSuspenseQuery<UserCompaniesQuery>(USER_COMPANIES_QUERY)

  const companies = userData?.user?.companies ?? []
  const hasMultipleCompanies = companies.length > 1

  const [step, setStep] = useState<Step>(
    hasMultipleCompanies ? 'company' : 'permissions'
  )

  const form = useForm<AuthorizeFormValues>({
    resolver: zodResolver(authorizeSchema),
    defaultValues: {
      idCompany: companies.length === 1 ? (companies[0]?.id ?? '') : '',
      permissionMode: 'role',
      idRole: '',
      capabilities: [],
    },
  })

  const idCompany = form.watch('idCompany')

  const handleCompanyContinue = useCallback(() => {
    if (idCompany) {
      setStep('permissions')
    }
  }, [idCompany])

  const [approveDeviceCode] = useMutation<
    ApproveDeviceCodeMutation,
    ApproveDeviceCodeMutationVariables
  >(APPROVE_DEVICE_CODE)
  const [denyDeviceCode] = useMutation<
    DenyDeviceCodeMutation,
    DenyDeviceCodeMutationVariables
  >(DENY_DEVICE_CODE)

  const onSubmit = useCallback(
    (values: AuthorizeFormValues) => {
      if (isPending) {
        return
      }

      startTransition(async () => {
        try {
          const name = `${appName || 'API key'} — ${format(new Date(), 'MMM d, yyyy HH:mm')}`

          const { data } = await approveDeviceCode({
            variables: {
              code,
              idCompany: values.idCompany,
              name,
              idRoles:
                values.permissionMode === 'role' && values.idRole
                  ? [values.idRole]
                  : undefined,
              capabilities:
                values.permissionMode === 'custom'
                  ? (values.capabilities ?? []).flatMap((cap) => {
                      const resource = cap.permission
                        .split(':')[0]
                        ?.toLowerCase()
                      const scopingMap = form.getValues('resourceScoping')
                      const scoping = scopingMap?.[resource ?? '']
                      if (!scoping || scoping.mode === 'all') {
                        return [{ permission: cap.permission }]
                      }
                      return scoping.ids.map((id) => ({
                        permission: cap.permission,
                        idResource: id,
                      }))
                    })
                  : undefined,
            },
          })

          if (data?.approveDeviceCode?.error) {
            toast(
              <ToasterContent variant="error">
                {data.approveDeviceCode.error}
              </ToasterContent>,
              { position: 'top-right' }
            )
            return
          }

          setStep('success')
          if (callback && isAllowedCallback(callback)) {
            window.location.href = callback
            return
          }
        } catch {
          toast(
            <ToasterContent variant="error">
              Failed to authorize. Please try again.
            </ToasterContent>,
            { position: 'top-right' }
          )
        }
      })
    },
    [isPending, approveDeviceCode, code, appName, form, callback]
  )

  const onDeny = useCallback(() => {
    if (isPending) {
      return
    }

    startTransition(async () => {
      try {
        const { data } = await denyDeviceCode({ variables: { code } })

        if (data?.denyDeviceCode?.error) {
          toast(
            <ToasterContent variant="error">
              {data.denyDeviceCode.error}
            </ToasterContent>,
            { position: 'top-right' }
          )
          return
        }

        setStep('denied')
        if (callback && isAllowedCallback(callback)) {
          window.location.href = callback
          return
        }
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to deny. Please try again.
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }, [isPending, denyDeviceCode, code, callback])

  if (isExpired) {
    return <ExpiredState appName={appName} />
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <AnimatePresence mode="wait">
          {step === 'company' ? (
            <motion.div
              key="company"
              className="space-y-6"
              {...ANIMATION_PROPS}
            >
              <div className="flex items-center gap-2">
                <Terminal className="text-muted-foreground size-4" />
                <p className="text-sm font-medium">Select a company</p>
              </div>

              <div className="bg-muted flex items-center justify-center gap-2 rounded-lg border p-3">
                <Muted className="text-xs">Your device code:</Muted>
                <code className="text-sm font-mono font-semibold tracking-widest">
                  {code}
                </code>
              </div>

              <FormField
                control={form.control}
                name="idCompany"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        className="grid gap-3"
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        {companies.map((company) => (
                          <Label
                            key={company.id}
                            className={cn(
                              'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                              'flex items-center gap-3 rounded-lg border p-4 cursor-pointer'
                            )}
                          >
                            <RadioGroupItem
                              value={company.id}
                              className="shrink-0 data-[state=checked]:border-primary"
                            />
                            <div className="text-sm font-medium">
                              {company.name}
                            </div>
                          </Label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />

              <Button
                type="button"
                className="w-full"
                disabled={!idCompany}
                onClick={handleCompanyContinue}
              >
                Continue
              </Button>
            </motion.div>
          ) : step === 'permissions' ? (
            <motion.div
              key="permissions"
              className="space-y-6"
              {...ANIMATION_PROPS}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Terminal className="text-muted-foreground size-4" />
                  <p className="text-sm font-medium">Set permissions</p>
                  {hasMultipleCompanies ? (
                    <Badge variant="secondary" className="text-xs">
                      {companies.find((c) => c.id === idCompany)?.name}
                    </Badge>
                  ) : null}
                </div>
                <code className="text-muted-foreground text-xs font-mono tracking-widest">
                  {code}
                </code>
              </div>

              <PermissionContent form={form} idCompany={idCompany} />

              <div className="flex gap-3">
                {hasMultipleCompanies ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep('company')}
                    disabled={isPending}
                  >
                    <ArrowLeft className="size-4" />
                    Back
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  onClick={onDeny}
                  disabled={isPending}
                >
                  Deny
                </Button>
                <Button type="submit" className="flex-1" disabled={isPending}>
                  {isPending ? (
                    <Loader className="size-4 animate-spin" />
                  ) : null}
                  Authorize
                </Button>
              </div>
            </motion.div>
          ) : step === 'success' ? (
            <SuccessState appName={appName} />
          ) : (
            <DeniedState appName={appName} />
          )}
        </AnimatePresence>
      </form>
    </Form>
  )
}

const PermissionContent = ({
  form,
  idCompany,
}: {
  form: ReturnType<typeof useForm<AuthorizeFormValues>>
  idCompany: string
}) => {
  const { data: rolesData } = useSuspenseQuery<
    GetApiKeyRolesAndPermissionsQuery,
    GetApiKeyRolesAndPermissionsQueryVariables
  >(GET_API_KEY_ROLES_AND_PERMISSIONS, { variables: { idCompany } })

  const roles = rolesData?.company?.apiKeyRoles ?? []
  const permissions = rolesData?.company?.apiKeyPermissions ?? []
  const companyResources: CompanyResources = {
    app: rolesData?.company?.apps?.items ?? [],
    database: rolesData?.company?.databases?.items ?? [],
    static_site: rolesData?.company?.staticSites?.items ?? [],
    object_storage: rolesData?.company?.objectStorages?.items ?? [],
    load_balancer: rolesData?.company?.edgeLoadBalancers?.items ?? [],
    webhook: (rolesData?.company?.webhooks?.items ?? []).map((w) => ({
      id: w.id,
      displayName: w.endpoint,
    })),
    api_key: (rolesData?.company?.apiKeys ?? []).map((k) => ({
      id: k.id,
      displayName: k.name,
    })),
    registry_credential: (
      rolesData?.company?.dockerRegistryCredentials ?? []
    ).map((r) => ({
      id: r.id,
      displayName: r.name,
    })),
  }

  return (
    <PermissionSelector
      form={form}
      roles={roles}
      permissions={permissions}
      companyResources={companyResources}
      idPrefix="authorize-"
    />
  )
}
